import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { useMemo } from 'react';
import type { Client, CombinedError, OperationContext } from 'urql';

import { FeatureFlag } from '@revelio/core';
import {
  COMPOSITION_V4,
  type CompositionV3Req,
  type CompositionV4Query,
  type CompositionV4QueryVariables,
} from '@revelio/data-access';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import type { DataProviderQueryStatus } from '../DataProvider/types';
import {
  combineQueryStatus,
  useUrqlQueryWithStatus,
} from '../DataProvider/utils/data-provider-results-utils';
import {
  type CompositionV3QueryData,
  getCompositionV3Selection,
  toCompositionV3Data,
  toCompositionV4Variables,
} from './composition-v3-to-v4';

type CompositionV3Variables = { req: CompositionV3Req };

type CompositionV3QueryState<Data> = {
  data: Data | undefined;
  error: CombinedError | undefined;
  fetching: boolean;
  status: DataProviderQueryStatus;
};

const asQueryData = <Data>(data: CompositionV3QueryData) =>
  data as unknown as Data;

export const useCompositionV3QueryWithStatus = <Data>({
  query,
  variables,
  pause = false,
  context,
}: {
  query: TypedDocumentNode<Data, CompositionV3Variables>;
  variables: CompositionV3Variables;
  pause?: boolean;
  context?: Partial<OperationContext>;
}): readonly [CompositionV3QueryState<Data>] => {
  const isCompositionV4Enabled = useUnleashFlag(FeatureFlag.CompositionV4);
  const [v3] = useUrqlQueryWithStatus<Data, CompositionV3Variables>({
    query,
    variables,
    context,
    pause: pause || isCompositionV4Enabled,
  });

  const selection = useMemo(() => getCompositionV3Selection(query), [query]);
  const { primary, overflow } = useMemo(
    () => toCompositionV4Variables(variables.req, selection),
    [variables.req, selection]
  );
  const pauseV4 = pause || !isCompositionV4Enabled;
  const [v4] = useUrqlQueryWithStatus<
    CompositionV4Query,
    CompositionV4QueryVariables
  >({
    query: COMPOSITION_V4,
    variables: primary,
    context,
    pause: pauseV4,
  });
  const [v4Overflow] = useUrqlQueryWithStatus<
    CompositionV4Query,
    CompositionV4QueryVariables
  >({
    query: COMPOSITION_V4,
    variables: overflow ?? primary,
    context,
    pause: pauseV4 || overflow === undefined,
  });

  const primaryData = v4.data?.composition_v4;
  const overflowData = overflow ? v4Overflow.data?.composition_v4 : null;
  const data = useMemo(() => {
    if (!primaryData || overflowData === undefined) return undefined;
    return asQueryData<Data>(
      toCompositionV3Data(
        overflowData ? [primaryData, overflowData] : [primaryData],
        selection
      )
    );
  }, [primaryData, overflowData, selection]);

  if (!isCompositionV4Enabled) {
    const { error, fetching, status } = v3;
    return [{ data: v3.data, error, fetching, status }];
  }
  const states = overflow ? [v4, v4Overflow] : [v4];
  return [
    {
      data,
      error: states.find(({ error }) => error)?.error,
      fetching: states.some(({ fetching }) => fetching),
      status: combineQueryStatus(states.map(({ status }) => status)),
    },
  ];
};

export const queryCompositionV3 = async <Data>(
  client: Client,
  query: TypedDocumentNode<Data, CompositionV3Variables>,
  variables: CompositionV3Variables,
  { compositionV4 }: { compositionV4: boolean }
): Promise<{ data?: Data; error?: CombinedError }> => {
  if (!compositionV4) {
    const { data, error } = await client.query(query, variables).toPromise();
    return { data, error };
  }
  const selection = getCompositionV3Selection(query);
  const { primary, overflow } = toCompositionV4Variables(
    variables.req,
    selection
  );
  const [primaryResult, overflowResult] = await Promise.all([
    client.query(COMPOSITION_V4, primary).toPromise(),
    overflow && client.query(COMPOSITION_V4, overflow).toPromise(),
  ]);
  const error = primaryResult.error ?? overflowResult?.error;
  const primaryData = primaryResult.data?.composition_v4;
  const overflowData = overflowResult?.data?.composition_v4;
  if (error || !primaryData || (overflow && !overflowData)) return { error };
  return {
    data: asQueryData<Data>(
      toCompositionV3Data(
        overflowData ? [primaryData, overflowData] : [primaryData],
        selection
      )
    ),
  };
};
